import { tr, type Entry, type MessageKey } from './tr.js';

// The translator without any catalog but Turkish (the source and fallback). The web app imports this and
// loads other catalogs on demand (`@dijitalapp/i18n/en`); the server imports the package root, which has all.

export { type MessageKey, type Entry } from './tr.js';

export type Catalog = Record<MessageKey, Entry>;
export type Params = Record<string, string | number>;

const RTL_LANGUAGES = new Set(['ar', 'fa', 'he', 'ur']);

export function isRtl(locale: string): boolean {
  return RTL_LANGUAGES.has(locale.split('-')[0] ?? locale);
}

function interpolate(template: string, params: Params | undefined): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

// Intl formatters are slow to make and cheap to use: made once per locale and options (a long chat formats
// thousands of times while scrolling).
const formatters = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat | Intl.PluralRules>();
function cached<T extends Intl.DateTimeFormat | Intl.NumberFormat | Intl.PluralRules>(key: string, make: () => T): T {
  let f = formatters.get(key) as T | undefined;
  if (!f) {
    f = make();
    formatters.set(key, f);
  }
  return f;
}

function pick(entry: Entry | undefined, locale: string, params: Params | undefined): string | undefined {
  // A text missing from the catalogs (a new role or status from the server, say) never breaks a page.
  if (entry === undefined) return undefined;
  if (typeof entry === 'string') return entry;
  const count = typeof params?.count === 'number' ? params.count : 0;
  const rule = cached(`p|${locale}`, () => new Intl.PluralRules(locale)).select(count);
  return entry[rule] ?? entry.other;
}

/** Translator for `locale` from the given catalogs; a language without a catalog falls back to Turkish. */
export function translatorFor(locale: string, catalogs: Partial<Record<string, Catalog>>) {
  const lang = locale.split('-')[0] ?? 'tr';
  const found = lang === 'tr' ? tr : catalogs[lang];
  const catalog = found ?? tr;
  const resolvedLocale = found ? lang : 'tr';

  const t = (key: MessageKey, params?: Params): string => {
    const entry = catalog[key] ?? tr[key];
    const formattedParams = params
      ? Object.fromEntries(
          Object.entries(params).map(([k, v]) => [
            k,
            typeof v === 'number' ? cached(`n|${resolvedLocale}`, () => new Intl.NumberFormat(resolvedLocale)).format(v) : v,
          ]),
        )
      : undefined;
    const text = pick(entry, resolvedLocale, params);
    // Missing: the key itself shows (easy to spot and fix) instead of the page failing.
    return text === undefined ? key : interpolate(text, formattedParams);
  };

  const formatDate = (value: string | Date, options?: Intl.DateTimeFormatOptions) => {
    const opts = options ?? { dateStyle: 'medium', timeStyle: 'short' };
    return cached(`d|${resolvedLocale}|${JSON.stringify(opts)}`, () => new Intl.DateTimeFormat(resolvedLocale, opts)).format(
      typeof value === 'string' ? new Date(value) : value,
    );
  };

  return { t, formatDate, locale: resolvedLocale, dir: isRtl(resolvedLocale) ? 'rtl' : 'ltr' } as const;
}

export type Translator = ReturnType<typeof translatorFor>;

/** Whether a message exists, for labels built from server values (unknown values fall back to the raw value). */
export function hasMessage(key: string): key is MessageKey {
  return Object.prototype.hasOwnProperty.call(tr, key);
}
